import { createStore } from '@/common/pinia';
import type { AgbStatsResponse, AgbTimeseriesResponse } from './agb.types';

/**
 * Cache for the CTrees Global Aboveground Biomass product served via the analytics
 * infrastructure (`<ForestStructureTool>`). No layer-URL slot — the per-year tile URL is
 * self-templated (`/agb/tiles/{z}/{x}/{y}?year=N`), so there's nothing to fetch/cache for
 * the raster. Stats are cached per `${year}:${JSON.stringify(coords)}` (the same AOI
 * returns different numbers per epoch); the timeseries is cached per `${coords}` alone
 * (one call spans all 26 years). Sibling of `EsaBiomassStore`.
 */
class AgbStore {
  /** Per-(year, polygon) stats keyed by `${year}:${JSON.stringify(coords)}`. */
  private statsCache: Map<string, AgbStatsResponse> = new Map();

  /** Per-polygon timeseries keyed by `${JSON.stringify(coords)}` (year-independent). */
  private timeseriesCache: Map<string, AgbTimeseriesResponse> = new Map();

  public getStatsCache(fingerprint: string): AgbStatsResponse | undefined {
    return this.statsCache.get(fingerprint);
  }

  public setStatsCache(fingerprint: string, stats: AgbStatsResponse): void {
    this.statsCache.set(fingerprint, stats);
  }

  public getTimeseriesCache(fingerprint: string): AgbTimeseriesResponse | undefined {
    return this.timeseriesCache.get(fingerprint);
  }

  public setTimeseriesCache(fingerprint: string, ts: AgbTimeseriesResponse): void {
    this.timeseriesCache.set(fingerprint, ts);
  }
}

const useAgbStore = createStore(AgbStore);

export { useAgbStore, type AgbStore };
